import { createStore } from '@ngneat/elf';
import {
  deleteEntities,
  getAllEntities,
  getEntity,
  selectAllEntities,
  selectEntity,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tenantStorageKey } from '../shared/helpers/tenant';

const TRANSCRIPTS_STORE_NAME = 'andorTranscripts.v1';

/** How many recording sessions are kept locally; the oldest is dropped past this. */
const MAX_SESSIONS = 3;

/**
 * Clock difference tolerated between the server's message timestamps and this
 * client's notion of when the transcription bot was present, when deciding
 * which transcript a just-ended recording belongs to.
 */
const RECORDING_MATCH_SKEW_MS = 60_000;

/** One transcript line, mirroring the server's `TranscriptEntry`. */
export interface TranscriptLine {
  /** Cortex message id — the dedupe key when merging live + backlog. */
  id: string;
  senderName: string;
  content: string;
  /** ISO-8601 timestamp; sortable lexicographically for chronological order. */
  timestamp: string;
}

/**
 * A recorded session's transcript, built client-side from the messages this
 * user received while attending. `sessionKey` (`${roomId}:${startedAt}`) is the
 * elf entity key so a fresh recording (new `startedAt`) is a distinct session.
 */
export interface TranscriptSession {
  sessionKey: string;
  roomId: string;
  roomName: string;
  startedByName: string;
  startedAt: number;
  messages: TranscriptLine[];
  /**
   * Epoch-ms when the server deletes this recording's transcript, set when the
   * recording ended while this client watched (see `markRecordingEnded`).
   * Only the server copy goes; this local copy stays downloadable. Absent while
   * recording, for sessions stored before this field existed, and for
   * recordings that ended while this client was away.
   */
  deletesAt?: number;
}

/** Metadata needed to create a session entry, derived from the live event / backlog. */
export interface TranscriptSessionMeta {
  roomId: string;
  roomName: string;
  startedByName: string;
  startedAt: number;
}

const sessionKeyOf = (roomId: string, startedAt: number): string => `${roomId}:${startedAt}`;

// Sort by the parsed instant, not the raw ISO string: lexicographic string
// order mis-ranks mixed fractional-second precision or non-UTC offsets
// (e.g. "…:00Z" vs "…:00.500Z").
const byTimestamp = (a: TranscriptLine, b: TranscriptLine): number =>
  new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();

const transcriptsStore = createStore(
  { name: TRANSCRIPTS_STORE_NAME },
  withEntities<TranscriptSession, 'sessionKey'>({ idKey: 'sessionKey' })
);

// Persist the recent sessions locally so a reload keeps the transcript history
// (and the current session survives a page refresh). localStorage is ample for a
// handful of sessions; switch to the IDB storage helper if it ever gets large.
persistState(transcriptsStore, {
  key: tenantStorageKey(TRANSCRIPTS_STORE_NAME),
  storage: localStorageStrategy,
});

@Injectable({ providedIn: 'root' })
export class TranscriptsStore {
  readonly store = transcriptsStore;

  /**
   * Deletion deadlines of recordings that ended before any transcript line of
   * them reached this client, by room. The final transcript fetch right after
   * the end may still create their session; it inherits the deadline then.
   */
  readonly #pendingEnds = new Map<string, number>();

  readonly sessions$: Observable<TranscriptSession[]> = this.store.pipe(selectAllEntities());

  session(sessionKey: string): TranscriptSession | undefined {
    return this.store.query(getEntity(sessionKey));
  }

  selectSession(sessionKey: string): Observable<TranscriptSession | undefined> {
    return this.store.pipe(selectEntity(sessionKey));
  }

  /** The elf key a room's recording maps to. */
  keyFor(roomId: string, startedAt: number): string {
    return sessionKeyOf(roomId, startedAt);
  }

  /**
   * Merge live or backlog lines into a session — creating it from `meta` if new
   * — deduping by message id and keeping chronological order. When a brand-new
   * session pushes the count past MAX_SESSIONS, the oldest session(s) are dropped.
   */
  appendMessages(meta: TranscriptSessionMeta, lines: TranscriptLine[]): void {
    const sessionKey = sessionKeyOf(meta.roomId, meta.startedAt);
    const existing = this.session(sessionKey);

    if (existing) {
      const seen = new Set(existing.messages.map((m) => m.id));
      const fresh = lines.filter((l) => !seen.has(l.id));
      if (fresh.length > 0) {
        const messages = [...existing.messages, ...fresh].sort(byTimestamp);
        this.store.update(updateEntities(sessionKey, (s) => ({ ...s, messages })));
      }
    } else {
      // New session: dedupe the incoming batch against itself, then insert.
      const seen = new Set<string>();
      const messages = lines
        .filter((l) => (seen.has(l.id) ? false : (seen.add(l.id), true)))
        .sort(byTimestamp);
      // Its recording may already have ended (lines only arrived with the
      // final fetch after the end): take over that recording's deadline.
      const deletesAt = this.#pendingEnds.get(meta.roomId);
      this.#pendingEnds.delete(meta.roomId);
      this.store.update(
        upsertEntities([
          {
            sessionKey,
            roomId: meta.roomId,
            roomName: meta.roomName,
            startedByName: meta.startedByName,
            startedAt: meta.startedAt,
            messages,
            ...(deletesAt !== undefined && { deletesAt }),
          },
        ])
      );
    }

    // Always enforce the cap — also trims a store hydrated over the cap from a
    // prior build, not just fresh inserts. Cheap: at most a handful of sessions.
    this.#enforceCap();
  }

  /**
   * A room's recording just ended: the server deletes its transcript at
   * `deletesAt`. The deadline goes onto the transcript of that recording — the
   * room's newest session that received lines while the bot was present
   * (`botPresentSince`), so a recording that produced no lines never marks an
   * older transcript. Without such a session yet, the deadline waits for the
   * session the final transcript fetch creates.
   */
  markRecordingEnded(roomId: string, deletesAt: number, botPresentSince: number): void {
    const target = this.store
      .query(getAllEntities())
      .filter((s) => s.roomId === roomId)
      .sort((a, b) => b.startedAt - a.startedAt)
      .find((s) => {
        const last = s.messages[s.messages.length - 1];
        return (
          last !== undefined &&
          new Date(last.timestamp).getTime() >= botPresentSince - RECORDING_MATCH_SKEW_MS
        );
      });
    if (target) {
      this.#pendingEnds.delete(roomId);
      this.store.update(updateEntities(target.sessionKey, (s) => ({ ...s, deletesAt })));
    } else {
      this.#pendingEnds.set(roomId, deletesAt);
    }
  }

  /**
   * A recording (re)started in the room: an end still waiting for its session
   * belongs to the previous recording and must not stick to the new one.
   */
  forgetPendingEnd(roomId: string): void {
    this.#pendingEnds.delete(roomId);
  }

  /** Wipe all stored sessions (used by the dev-console transcript fixture). */
  clear(): void {
    this.#pendingEnds.clear();
    this.store.reset();
  }

  /** Keep only the MAX_SESSIONS most recent sessions (by startedAt). */
  #enforceCap(): void {
    const all = this.store.query(getAllEntities());
    if (all.length <= MAX_SESSIONS) return;
    const drop = [...all]
      .sort((a, b) => a.startedAt - b.startedAt)
      .slice(0, all.length - MAX_SESSIONS)
      .map((s) => s.sessionKey);
    if (drop.length > 0) this.store.update(deleteEntities(drop));
  }
}
